import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {AssemblyRecipe} from "../State/AssemblyRecipes";
import {GameItems} from "../State/GameData";
import {ItemIcon} from "../SharedComponents/ItemIcon";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.3);
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const SearchBox = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Content = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
`;

const CategorySection = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecipeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
`;

const RecipeCard = styled.button<{ $active?: boolean }>`
  border: 2px solid ${props => props.$active ? "#22c55e" : "#e2e8f0"};
  background: ${props => props.$active ? "#ecfdf3" : "white"};
  padding: 12px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  &:hover {
    border-color: ${props => props.$active ? "#16a34a" : "#3b82f6"};
    background: ${props => props.$active ? "#dcfce7" : "#eff6ff"};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
  }
  &:active {
    transform: translateY(0);
  }
`;

const RecipeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RecipeName = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
  flex: 1;
`;

const RecipeDetails = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const IngredientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: #475569;
  margin-top: 4px;
`;

const IngredientLabel = styled.div`
  font-weight: 600;
  color: #475569;
  margin-bottom: 2px;
`;

const IngredientItem = styled.div`
  padding-left: 8px;
  &::before {
    content: "• ";
    margin-right: 4px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 14px;
`;

type RecipeSelectionModalProps = {
    recipes: AssemblyRecipe[];
    selectedRecipeId?: string;
    onSelect: (recipeId: string) => void;
    onClose: () => void;
};

// Categorize recipes based on their output type
const categorizeRecipes = (recipes: AssemblyRecipe[]) => {
    const categories: Record<string, AssemblyRecipe[]> = {
        "Plates & Materials": [],
        "Components": [],
        "Intermediate Products": [],
        "Other": [],
    };

    recipes.forEach(recipe => {
        const itemName = GameItems[recipe.result.itemId]?.name?.toLowerCase() || "";

        if (itemName.includes("plate") || itemName.includes("brick")) {
            categories["Plates & Materials"].push(recipe);
        } else if (itemName.includes("circuit") || itemName.includes("gear") || itemName.includes("stick")) {
            categories["Components"].push(recipe);
        } else if (itemName.includes("belt") || itemName.includes("inserter") || itemName.includes("science")) {
            categories["Intermediate Products"].push(recipe);
        } else {
            categories["Other"].push(recipe);
        }
    });

    // Remove empty categories
    return Object.entries(categories).filter(([_, recipes]) => recipes.length > 0);
};

export function RecipeSelectionModal({recipes, selectedRecipeId, onSelect, onClose}: RecipeSelectionModalProps) {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredRecipes = useMemo(() => {
        if (!searchTerm.trim()) return recipes;
        const term = searchTerm.toLowerCase();
        return recipes.filter(recipe => {
            const recipeName = recipe.name.toLowerCase();
            const itemName = GameItems[recipe.result.itemId]?.name?.toLowerCase() || "";
            const ingredients = recipe.ingredients.map(ing =>
                GameItems[ing.itemId]?.name?.toLowerCase() || ""
            ).join(" ");

            return recipeName.includes(term) || itemName.includes(term) || ingredients.includes(term);
        });
    }, [recipes, searchTerm]);

    const categorizedRecipes = useMemo(() => {
        return categorizeRecipes(filteredRecipes);
    }, [filteredRecipes]);

    const handleSelect = (recipeId: string) => {
        onSelect(recipeId);
        onClose();
    };

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>Select Recipe</Title>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>
                <Content>
                    <SearchBox
                        type="text"
                        placeholder="Search recipes, items, or ingredients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />

                    {filteredRecipes.length === 0 ? (
                        <EmptyState>No recipes found matching "{searchTerm}"</EmptyState>
                    ) : searchTerm.trim() ? (
                        // Show all results in one list when searching
                        <RecipeGrid>
                            {filteredRecipes.map(recipe => (
                                <RecipeCard
                                    key={recipe.id}
                                    $active={recipe.id === selectedRecipeId}
                                    onClick={() => handleSelect(recipe.id)}
                                >
                                    <RecipeHeader>
                                        <ItemIcon itemId={recipe.result.itemId} size={32} />
                                        <RecipeName>{recipe.name}</RecipeName>
                                    </RecipeHeader>
                                    <RecipeDetails>
                                        <div>
                                            <strong>Output:</strong> {GameItems[recipe.result.itemId].name} x{recipe.result.quantity}
                                        </div>
                                        <div>
                                            <strong>Time:</strong> {(recipe.durationMs / 1000).toFixed(1)}s
                                        </div>
                                        <div>
                                            <IngredientLabel>Ingredients:</IngredientLabel>
                                            <IngredientList>
                                                {recipe.ingredients.map(ing => {
                                                    const perSecond = (ing.quantity / (recipe.durationMs / 1000)).toFixed(2);
                                                    return (
                                                        <IngredientItem key={ing.itemId}>
                                                            {ing.quantity}x {GameItems[ing.itemId].name} ({perSecond}/s)
                                                        </IngredientItem>
                                                    );
                                                })}
                                            </IngredientList>
                                        </div>
                                    </RecipeDetails>
                                </RecipeCard>
                            ))}
                        </RecipeGrid>
                    ) : (
                        // Show categorized results when not searching
                        categorizedRecipes.map(([category, categoryRecipes]) => (
                            <CategorySection key={category}>
                                <CategoryTitle>{category}</CategoryTitle>
                                <RecipeGrid>
                                    {categoryRecipes.map(recipe => (
                                        <RecipeCard
                                            key={recipe.id}
                                            $active={recipe.id === selectedRecipeId}
                                            onClick={() => handleSelect(recipe.id)}
                                        >
                                            <RecipeHeader>
                                                <ItemIcon itemId={recipe.result.itemId} size={32} />
                                                <RecipeName>{recipe.name}</RecipeName>
                                            </RecipeHeader>
                                            <RecipeDetails>
                                                <div>
                                                    <strong>Output:</strong> {GameItems[recipe.result.itemId].name} x{recipe.result.quantity}
                                                </div>
                                                <div>
                                                    <strong>Time:</strong> {(recipe.durationMs / 1000).toFixed(1)}s
                                                </div>
                                                <div>
                                                    <IngredientLabel>Ingredients:</IngredientLabel>
                                                    <IngredientList>
                                                        {recipe.ingredients.map(ing => {
                                                            const perSecond = (ing.quantity / (recipe.durationMs / 1000)).toFixed(2);
                                                            return (
                                                                <IngredientItem key={ing.itemId}>
                                                                    {ing.quantity}x {GameItems[ing.itemId].name} ({perSecond}/s)
                                                                </IngredientItem>
                                                            );
                                                        })}
                                                    </IngredientList>
                                                </div>
                                            </RecipeDetails>
                                        </RecipeCard>
                                    ))}
                                </RecipeGrid>
                            </CategorySection>
                        ))
                    )}
                </Content>
            </Modal>
        </Overlay>
    );
}
